import _axios from "axios";

export type ServerError = {
  statusCode: number;
  message: string;
  error: string;
};

// Clerk token getter - initialized once by the application auth boundary.
let getSessionToken: (() => Promise<string>) | null = null;

const axiosInstance = _axios.create({
  baseURL: "/",
  withCredentials: true,
});

export const initializeSessionAuth = (tokenGetter: () => Promise<string>) => {
  getSessionToken = tokenGetter;
};

// Export token getter for external use
export const getToken = async (): Promise<string | null> => {
  if (!getSessionToken) {
    console.warn("Clerk session auth is not initialized");
    return null;
  }

  try {
    return await getSessionToken();
  } catch (error) {
    console.error("Failed to get token:", error);
    return null;
  }
};

// Global PaymentDialog opener registration
type PaymentDialogOpener = (payload: {
  featureKey: string;
  message?: string;
  used?: number | null;
  limit?: number | null;
  contactAdmin?: boolean;
  tier?: string | null;
  billingEntity?: "org";
}) => void;

let paymentDialogOpener: PaymentDialogOpener | null = null;
export const registerPaymentDialogOpener = (opener: PaymentDialogOpener) => {
  paymentDialogOpener = opener;
};

// Request interceptor to add the Clerk session bearer token.
axiosInstance.interceptors.request.use(
  async (config) => {
    if (getSessionToken) {
      try {
        const token = await getSessionToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {}
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Check if error has response (server responded with error status)
    if (error.response) {
      const { status, data } = error.response;
      // Handle 403 pricing gate errors by opening PaymentDialog
      if (status === 403 && paymentDialogOpener) {
        const d = data as any;
        if (d && (d.upgradeRequired === true || typeof d.feature === "string")) {
          try {
            paymentDialogOpener({
              featureKey: d.feature || "unknown_feature",
              message: typeof d.message === "string" ? d.message : undefined,
              used: typeof d.currentUsage === "number" ? d.currentUsage : null,
              limit: typeof d.limit === "number" ? d.limit : null,
              contactAdmin: !!d.contactAdmin,
              tier: typeof d.tier === "string" ? d.tier : null,
              billingEntity: typeof d.billingEntity === "string" ? d.billingEntity : undefined,
            });
          } catch (_) {
            // swallow
          }
        }
        return Promise.reject(error);
      }

      if (status === 401) {
        return Promise.reject(error);
      }
    } else {
      // Something else happened
    }

    return Promise.reject(error);
  }
);

export { axiosInstance as Axios };
export { _axios };
export default axiosInstance;
